import { PageSkeleton } from '@euroasiann/shared/ui';
/**
 * Onboarding Details Page
 * Admin portal page to view detailed customer or vendor onboarding submissions
 */

import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { useToast } from '../../components/shared/Toast';
import { FileDownloadLink } from '../../components/shared/FileDownloadLink';
import { 
  MdArrowBack, MdCheckCircle, MdCancel, MdBusiness, MdPerson, MdEmail, 
  MdPhone, MdLocationOn, MdPayments, MdAccountBalance, MdReceiptLong,
  MdConfirmationNumber, MdCalendarToday, MdInfo, MdBusinessCenter, MdSend, MdDescription
} from 'react-icons/md';
import { cn } from '../../lib/utils';
import { FormattedAmount } from '../../components/shared/FormattedAmount';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

type OnboardingType = 'customer' | 'vendor';

function onboardingDetailQueryKey(type: OnboardingType, id: string) {
  return [`${type}-onboarding`, id] as const;
}

/** Merge mutation API `data` into the detail query cache so status/UI update immediately. */
function applyOnboardingDetailFromMutationResponse(
  queryClient: QueryClient,
  type: OnboardingType,
  id: string | undefined,
  response: { data?: Record<string, unknown> }
) {
  if (!id || !response?.data) return;
  const { emailSent: _emailSent, credentialsEmail: _credentialsEmail, emailError: _emailError, ...fields } =
    response.data;
  queryClient.setQueryData(onboardingDetailQueryKey(type, id), (prev: Record<string, unknown> | undefined) =>
    prev ? { ...prev, ...fields } : fields
  );
}

export function OnboardingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const type = searchParams.get('type') as 'customer' | 'vendor' || 'customer';

  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Fetch onboarding data
  const { data: onboardingData, isLoading, error } = useQuery({
    queryKey: onboardingDetailQueryKey(type, id!),
    queryFn: async () => {
      const endpoint = type === 'customer' 
        ? `${API_URL}/api/v1/admin/customer-onboardings/${id}`
        : `${API_URL}/api/v1/admin/vendor-onboardings/${id}`;
      
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch onboarding details');
      }
      const data = await response.json();
      return data.data;
    },
    enabled: !!id,
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async () => {
      const endpoint = type === 'customer'
        ? `${API_URL}/api/v1/admin/customer-onboardings/${id}/approve`
        : `${API_URL}/api/v1/admin/vendor-onboardings/${id}/approve`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve onboarding');
      }
      return response.json();
    },
    onSuccess: (data) => {
      applyOnboardingDetailFromMutationResponse(queryClient, type, id, data);
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      const emailFailed = data?.data?.emailError && !data?.data?.emailSent;
      showToast(
        data.message || 'Onboarding approved successfully',
        emailFailed ? 'error' : 'success'
      );
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to approve onboarding', 'error');
    },
  });

  const approvePaymentMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/api/v1/admin/customer-onboardings/${id}/approve-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve payment status');
      }
      return response.json();
    },
    onSuccess: (data) => {
      applyOnboardingDetailFromMutationResponse(queryClient, type, id, data);
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      const emailFailed = data?.data?.emailError && !data?.data?.emailSent;
      showToast(
        data.message || 'Payment approved and account activated',
        emailFailed ? 'error' : 'success'
      );
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to approve payment status', 'error');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (reason: string) => {
      const endpoint = type === 'customer'
        ? `${API_URL}/api/v1/admin/customer-onboardings/${id}/reject`
        : `${API_URL}/api/v1/admin/vendor-onboardings/${id}/reject`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ rejectionReason: reason }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reject onboarding');
      }
      return response.json();
    },
    onSuccess: (data) => {
      applyOnboardingDetailFromMutationResponse(queryClient, type, id, data);
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      showToast(data.message || 'Onboarding rejected successfully', 'success');
      setShowRejectInput(false);
      setRejectionReason('');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to reject onboarding', 'error');
    },
  });

  const handleApprove = () => {
    if (window.confirm('Approve this onboarding? A license and admin user will be created, and login credentials will be emailed to the customer.')) {
      approveMutation.mutate();
    }
  };

  const handleAcceptPayment = () => {
    if (
      window.confirm(
        'Accept this payment and activate the customer account? Login credentials will be emailed to the customer.'
      )
    ) {
      approvePaymentMutation.mutate();
    }
  };

  const handleReject = () => {
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    if (rejectionReason.trim()) {
      rejectMutation.mutate(rejectionReason);
    } else {
      showToast('Please provide a reason for rejection', 'error');
    }
  };

  if (isLoading) {
    return <PageSkeleton variant="cards" />;
  }

  if (error || !onboardingData) {
    return (
      <div className="p-12 text-center bg-[hsl(var(--card))] rounded-2xl border border-red-500/20 shadow-sm">
        <p className="text-red-600 font-bold text-lg mb-4">Error loading data</p>
        <p className="text-[hsl(var(--muted-foreground))] mb-6">{error instanceof Error ? error.message : 'Onboarding submission not found'}</p>
        <button onClick={() => navigate('/onboarding-data')} className="btn-secondary">
          Back to List
        </button>
      </div>
    );
  }

  const isPending =
    onboardingData.status === 'completed' ||
    onboardingData.status === 'pending' ||
    onboardingData.status === 'pending_payment';
  const isApproved = onboardingData.status === 'approved';
  const isRejected = onboardingData.status === 'rejected';
  const needsPaymentApproval =
    type === 'customer' &&
    !isApproved &&
    !isRejected &&
    onboardingData.paymentStatus !== 'paid';

  const vendorOrganizationId =
    type === 'vendor' && onboardingData.organizationId != null
      ? String(onboardingData.organizationId)
      : '';

  const handleSendRfqToVendor = () => {
    if (!vendorOrganizationId) {
      showToast('Vendor organization is not linked to this submission yet.', 'error');
      return;
    }
    if (!isApproved) {
      showToast('Approve this vendor before sending an RFQ.', 'warning');
      return;
    }
    const params = new URLSearchParams({
      vendorId: vendorOrganizationId,
      vendorName: onboardingData.companyName || 'Vendor',
    });
    navigate(`/dashboard/admin/rfqs/new?${params.toString()}`);
  };

  return (
    <div className="w-full space-y-8 pb-12 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <button
            onClick={() => navigate('/onboarding-data')}
            className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-blue-600 transition-colors mb-4 group font-medium"
          >
            <MdArrowBack className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Onboarding Data
          </button>
          <h1 className="text-4xl font-extrabold text-[hsl(var(--foreground))] tracking-tight">
            Submission Details: <span className="text-[#0088cc]">{onboardingData.companyName}</span>
          </h1>
          <div className="flex items-center gap-3 mt-3">
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider",
              type === 'customer' ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
            )}>
              {type.toUpperCase()}
            </span>
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider",
              isApproved ? "bg-emerald-100 text-emerald-700" : isRejected ? "bg-red-100 text-red-700" : "bg-blue-50 text-blue-600"
            )}>
              {onboardingData.status.toUpperCase()}
            </span>
            {type === 'customer' && (
              <span className={cn(
                "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider",
                onboardingData.paymentStatus === 'paid'
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-800"
              )}>
                Payment {(onboardingData.paymentStatus || 'pending').replace('_', ' ')}
              </span>
            )}
            <span className="text-sm text-[#0088cc]/65 flex items-center gap-1">
              <MdCalendarToday className="w-4 h-4" />
              Submitted {new Date(onboardingData.submittedAt || onboardingData.createdAt).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {type === 'vendor' && (
            <button
              type="button"
              onClick={handleSendRfqToVendor}
              disabled={!vendorOrganizationId || !isApproved}
              title={
                !vendorOrganizationId
                  ? 'Organization not linked yet'
                  : !isApproved
                    ? 'Approve vendor before sending RFQ'
                    : 'Create RFQ for this vendor'
              }
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
                isApproved && vendorOrganizationId
                  ? 'bg-[#0088cc] hover:bg-[#0077b3] text-white hover:shadow-[#0088cc]/25'
                  : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'
              )}
            >
              <MdSend className="w-5 h-5" />
              Send RFQ
            </button>
          )}

        {isPending && (
          <div className="flex flex-wrap items-center gap-3">
            {showRejectInput ? (
              <div className="flex flex-col gap-3 w-full md:w-[300px]">
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="w-full p-3 rounded-xl border border-red-200 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none transition-all text-sm h-24"
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowRejectInput(false)} className="flex-1 px-4 py-2 rounded-lg bg-[#0088cc]/10 hover:bg-[#0088cc]/15 text-[hsl(var(--foreground))] font-bold transition-all text-sm">Cancel</button>
                  <button onClick={handleReject} disabled={rejectMutation.isPending} className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold transition-all text-sm">Confirm Reject</button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleReject}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-red-500 text-red-600 hover:bg-red-50 font-bold transition-all shadow-sm"
                >
                  <MdCancel className="w-5 h-5" />
                  Reject
                </button>
                {needsPaymentApproval && (
                  <button
                    onClick={handleAcceptPayment}
                    disabled={approvePaymentMutation.isPending}
                    className="flex items-center gap-2 px-8 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all shadow-lg hover:shadow-amber-500/20 active:scale-95 disabled:opacity-50"
                  >
                    <MdPayments className="w-5 h-5" />
                    {approvePaymentMutation.isPending ? 'Accepting...' : 'Accept Payment Approval'}
                  </button>
                )}
                {!needsPaymentApproval && (
                  <button
                    onClick={handleApprove}
                    disabled={approveMutation.isPending}
                    className="flex items-center gap-2 px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-all shadow-lg hover:shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
                  >
                    <MdCheckCircle className="w-5 h-5" />
                    {approveMutation.isPending ? 'Approving...' : 'Approve Entry'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {needsPaymentApproval && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-amber-900">Payment approval required</h2>
            <p className="text-sm text-amber-800 mt-1">
              Accept payment to activate this customer and email login credentials to{' '}
              <span className="font-semibold">{onboardingData.email}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAcceptPayment}
            disabled={approvePaymentMutation.isPending}
            className="shrink-0 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold transition-colors disabled:opacity-50"
          >
            <MdPayments className="w-5 h-5" />
            {approvePaymentMutation.isPending ? 'Accepting Payment...' : 'Accept Payment Approval'}
          </button>
        </section>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column - Principal Info */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Card: Identity & Primary Info */}
          <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
            <div className="px-8 py-5 bg-[#0088cc]/5 border-b border-[#0088cc]/15">
              <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase tracking-wide">
                <MdBusiness className="text-[#0088cc] w-6 h-6" />
                Identity & Contact Information
              </h2>
            </div>
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <InfoItem label="Legal Company Name" value={onboardingData.companyName} icon={MdBusiness} />
              <InfoItem label="Business Group" value={onboardingData.businessGroupName} icon={MdBusinessCenter} />
              <InfoItem label="Primary Contact Person" value={onboardingData.contactPerson} icon={MdPerson} />
              <InfoItem label="Official Email Address" value={onboardingData.email} icon={MdEmail} />
              <InfoItem label="Mobile Phone" value={`${onboardingData.mobileCountryCode} ${onboardingData.mobilePhone}`} icon={MdPhone} />
              <InfoItem label="Desk Phone" value={`${onboardingData.deskCountryCode} ${onboardingData.deskPhone}`} icon={MdPhone} />
              <InfoItem label="Company Activity / Niche" value={onboardingData.companyActivity} />
              {type === 'customer' && (
                <InfoItem label="Planned Vessel Count" value={onboardingData.vessels} icon={MdConfirmationNumber} highlight />
              )}
            </div>
          </section>

          {type === 'vendor' && (
            <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
              <div className="px-8 py-5 bg-emerald-500/10 border-b border-emerald-500/20">
                <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase tracking-wide">
                  <MdDescription className="text-emerald-600 w-6 h-6" />
                  Vendor Agreement Document
                </h2>
              </div>
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <InfoItem
                  label="Agreement Document Accepted"
                  value={onboardingData.termsAccepted ? 'Yes' : 'No'}
                  highlight={!!onboardingData.termsAccepted}
                />
                <InfoItem
                  label="Digitally Signed (Documenso)"
                  value={onboardingData.agreementSigned ? 'Yes' : 'No'}
                  highlight={!!onboardingData.agreementSigned}
                />
                {onboardingData.agreementSignedAt && (
                  <InfoItem
                    label="Signed At"
                    value={new Date(onboardingData.agreementSignedAt).toLocaleString()}
                  />
                )}
                {(onboardingData.documensoSignerName || onboardingData.documensoSignerEmail) && (
                  <InfoItem
                    label="Signer"
                    value={[onboardingData.documensoSignerName, onboardingData.documensoSignerEmail]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                )}
                {(onboardingData.documensoDocumentId || onboardingData.opensignDocumentId) && (
                  <InfoItem
                    label="Signature Document ID"
                    value={onboardingData.documensoDocumentId || onboardingData.opensignDocumentId}
                    fontMono
                  />
                )}
              </div>
            </section>
          )}

          {/* Card: Banking & Financial Metadata */}
          <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
            <div className="px-8 py-5 bg-[#0088cc]/5 border-b border-[#0088cc]/15">
              <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase tracking-wide">
                <MdAccountBalance className="text-[#0088cc] w-6 h-6" />
                Financial & Banking Setup
              </h2>
            </div>
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <InfoItem label="Bank Account Name" value={onboardingData.accountName} />
              <InfoItem label="Bank Name" value={onboardingData.bankName} />
              <InfoItem label="IBAN / Account Number" value={onboardingData.iban} fontMono />
              <InfoItem label="SWIFT / BIC Code" value={onboardingData.swift} fontMono />
              <div className="md:col-span-2 pt-4 border-t border-[#0088cc]/10">
                <InfoItem label="Global Tax ID / VAT Number" value={onboardingData.taxId} fontMono highlight />
              </div>
            </div>
          </section>

          {/* Card: Address Layouts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
              <div className="px-8 py-5 bg-[#0088cc]/5 border-b border-[#0088cc]/15">
                <h2 className="text-sm font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase">
                  <MdLocationOn className="text-[#0088cc]" />
                  Primary Physical Headquarters
                </h2>
              </div>
              <div className="p-8 space-y-4">
                <p className="text-[hsl(var(--foreground))] font-medium">
                  {onboardingData.address1}<br />
                  {onboardingData.address2 && <>{onboardingData.address2}<br /></>}
                  {onboardingData.city}, {onboardingData.province}<br />
                  {onboardingData.country} – {onboardingData.postalCode}
                </p>
              </div>
            </section>

            <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
              <div className="px-8 py-5 bg-amber-500/10 border-b border-amber-500/20">
                <h2 className="text-sm font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase">
                  <MdReceiptLong className="text-amber-400" />
                   Official Invoicing/Billing Address
                </h2>
              </div>
              <div className="p-8 space-y-4">
                <p className="text-blue-600 font-bold text-sm mb-2">{onboardingData.invoiceEmail}</p>
                <p className="text-[hsl(var(--foreground))] font-medium">
                  {onboardingData.billingAddress1}<br />
                  {onboardingData.billingAddress2 && <>{onboardingData.billingAddress2}<br /></>}
                  {onboardingData.billingCity}, {onboardingData.billingProvince}<br />
                  {onboardingData.billingCountry} – {onboardingData.billingPostal}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Right Column - Status, Payment, Documents */}
        <div className="space-y-8">
          
          {/* Status & Timing */}
          <section className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white shadow-2xl">
            <h3 className="text-[#0088cc]/65 text-xs font-bold uppercase mb-6 tracking-widest">Administrative Context</h3>
            <div className="space-y-6">
              <div>
                <p className="text-[hsl(var(--muted-foreground))] text-xs mb-1">Onboarding Status</p>
                <p className="text-xl font-bold flex items-center gap-2">
                  <span className={cn(
                    "w-3 h-3 rounded-full animate-pulse",
                    isApproved ? "bg-emerald-400" : isRejected ? "bg-red-400" : "bg-blue-400"
                  )} />
                  {onboardingData.status.toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-[hsl(var(--muted-foreground))] text-xs mb-1">Organization Mode</p>
                <p className="text-lg font-semibold capitalize">{type} Portal Access</p>
              </div>
              {isRejected && onboardingData.rejectionReason && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-xs font-bold mb-1">Rejection Reason</p>
                  <p className="text-sm text-[#0088cc]/45 italic">"{onboardingData.rejectionReason}"</p>
                </div>
              )}
            </div>
          </section>

          {/* Payment Card (Customer Only) */}
          {type === 'customer' && (
            <section className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))] shadow-xl overflow-hidden">
              <div className="px-8 py-5 bg-emerald-500/10 border-b border-emerald-500/20">
                <h2 className="text-sm font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase tracking-wide">
                  <MdPayments className="text-emerald-400 w-5 h-5" />
                  Financial Settlement
                </h2>
              </div>
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-[#0088cc]/65 mb-1">Total Fee (Yearly)</p>
                    <p className="text-2xl font-black text-emerald-600">
                      <FormattedAmount amount={Number(onboardingData.totalAmount || 0)} />
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#0088cc]/65 mb-1">Payment Strategy</p>
                    <p className="text-sm font-bold uppercase">{onboardingData.paymentMethod?.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5 p-4">
                  <p className="text-xs text-[#0088cc]/65 mb-1">Payment Status</p>
                  <p className="text-sm font-bold uppercase text-[hsl(var(--foreground))]">
                    {(onboardingData.paymentStatus || 'pending').replace('_', ' ')}
                  </p>
                </div>

                {onboardingData.paymentProof && (
                  <div className="pt-6 border-t border-[#0088cc]/10">
                    <p className="text-xs text-[#0088cc]/65 mb-3">Verification Document</p>
                    {(() => {
                      const proof = onboardingData.paymentProof;
                      const fileUrl = typeof proof === 'string' ? proof : proof?.fileUrl;
                      if (!fileUrl) return null;
                      
                      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_URL}/${fileUrl}`;
                      
                      return (
                        <FileDownloadLink
                          href={fullUrl}
                          fileName={
                            typeof proof === 'object' && proof?.fileName
                              ? proof.fileName
                              : 'payment-receipt.pdf'
                          }
                          className="flex items-center justify-center gap-3 w-full py-4 rounded-xl bg-[#0088cc]/5 hover:bg-[#0088cc]/10 text-[#0088cc] font-bold border-2 border-dashed border-[#0088cc]/20 transition-all hover:-translate-y-1"
                          onError={(message) => showToast(message, 'error')}
                        >
                          <MdReceiptLong className="w-6 h-6" />
                          View Uploaded Receipt
                        </FileDownloadLink>
                      );
                    })()}
                  </div>
                )}
                {needsPaymentApproval && (
                  <button
                    type="button"
                    onClick={handleAcceptPayment}
                    disabled={approvePaymentMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold transition-colors disabled:opacity-50"
                  >
                    <MdPayments className="w-5 h-5" />
                    {approvePaymentMutation.isPending ? 'Accepting Payment...' : 'Accept Payment Approval'}
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Vendor RFQ action */}
          {type === 'vendor' && (
            <section className="bg-[hsl(var(--card))] rounded-2xl border border-[#0088cc]/25 shadow-xl overflow-hidden">
              <div className="px-8 py-5 bg-[#0088cc]/10 border-b border-[#0088cc]/20">
                <h2 className="text-sm font-bold text-[hsl(var(--foreground))] flex items-center gap-2 uppercase tracking-wide">
                  <MdSend className="text-[#0088cc] w-5 h-5" />
                  RFQ to vendor
                </h2>
              </div>
              <div className="p-8 space-y-4">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Create a request for quotation and send it to{' '}
                  <span className="font-semibold text-[hsl(var(--foreground))]">{onboardingData.companyName}</span>.
                  The vendor is pre-selected on the RFQ form.
                </p>
                <button
                  type="button"
                  onClick={handleSendRfqToVendor}
                  disabled={!vendorOrganizationId || !isApproved}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0088cc] hover:bg-[#0077b3] text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdSend className="w-5 h-5" />
                  Send RFQ
                </button>
                {!isApproved && (
                  <p className="text-xs text-amber-600 font-medium">Approve this vendor to enable Send RFQ.</p>
                )}
              </div>
            </section>
          )}

          {/* Help / Activity */}
          <section className="bg-[#0088cc]/5 rounded-2xl p-6 border border-[#0088cc]/15">
            <h4 className="text-[hsl(var(--foreground))] font-bold text-sm mb-4 flex items-center gap-2">
              <MdInfo className="text-[#0088cc]/65" />
              Administrative Notes
            </h4>
            <ul className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                Approval will generate a {onboardingData.vessels}-vessel enterprise license.
              </li>
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                Accepting payment / approving entry emails login credentials to {onboardingData.email}.
              </li>
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                Banking data is stored for auto-debit if Stripe is active.
              </li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
}

// Helper Component for consistency
function InfoItem({ label, value, icon: Icon, fontMono = false, highlight = false }: any) {
  return (
    <div className="group">
      <p className="text-[10px] uppercase font-bold text-[#0088cc]/65 tracking-widest mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#0088cc]/45" />}
        {label}
      </p>
      <p className={cn(
        "text-sm font-semibold transition-colors rounded-lg",
        fontMono ? "font-mono" : "font-sans",
        highlight ? "text-[#0088cc] bg-[#0088cc]/10 border border-[#0088cc]/20 p-2 -ml-2" : "text-[hsl(var(--foreground))]",
        !value && "text-[#0088cc]/45 italic font-normal"
      )}>
        {value || 'Not Provided'}
      </p>
    </div>
  );
}
